import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    'checkboxColumn',
    'titleColumn',
    'priorityColumn',
    'accountColumn',
    'statusColumn',
    'startDateColumn',
    'dueDateColumn',
    'assigneeColumn',
    'clientAdvisorColumn',
    'taskTypeColumn',
    'customColumn',
    'row'
  ]

  static values = {
    storageKey: { type: String, default: 'taskTableFrozenColumns' }
  }

  // Core column order for freeze calculations (checkbox is always frozen, not in this list)
  coreColumnOrder = ['title', 'priority', 'account', 'status', 'startDate', 'dueDate', 'assignee', 'clientAdvisor', 'taskType']

  // Columns that are always frozen and shouldn't have freeze buttons
  // Dynamically set in connect() based on whether checkbox column exists
  alwaysFrozenColumns = ['title']

  // Dynamic column order (built from DOM)
  get columnOrder() {
    if (this._columnOrder) return this._columnOrder
    this._columnOrder = this.buildColumnOrder()
    return this._columnOrder
  }

  // Build column order from the header
  buildColumnOrder() {
    const header = this.element.querySelector('.task-table-header')
    if (!header) return this.coreColumnOrder

    const columns = []
    const allColumns = header.querySelectorAll('[data-task-table-target]')

    allColumns.forEach((col) => {
      const columnName = this.getColumnName(col)
      // Skip checkbox (always frozen separately) and already added columns
      if (columnName && columnName !== 'checkbox' && !columns.includes(columnName)) {
        columns.push(columnName)
      }
    })

    return columns.length > 0 ? columns : this.coreColumnOrder
  }

  // Extract column name from target attribute (e.g., "titleColumn" -> "title")
  // For custom columns, use data-column-id to get the specific column name
  getColumnName(element) {
    const target = element.dataset.taskTableTarget
    if (!target) return null
    if (target === 'customColumn') {
      // Use data-column-id for custom columns to identify them individually
      return element.dataset.columnId || 'custom'
    }
    return target.replace('Column', '')
  }

  // Reset column order cache when DOM changes
  resetColumnOrder() {
    this._columnOrder = null
  }

  connect() {
    // Check if checkbox column exists (Flipper enabled)
    const hasCheckboxColumn = this.hasCheckboxColumnTarget
    this.alwaysFrozenColumns = hasCheckboxColumn ? ['checkbox', 'title'] : ['title']

    this.setupResizableColumns()
    this.setupColumnFreezing()
    this.restoreFrozenState()

    // Re-apply frozen state when Turbo loads new content
    this.boundReapplyFrozenState = this.reapplyFrozenState.bind(this)
    document.addEventListener('turbo:frame-load', this.boundReapplyFrozenState)
    document.addEventListener('turbo:render', this.boundReapplyFrozenState)

    this.boundWrapStreamRender = this.wrapStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundWrapStreamRender)
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.boundReapplyFrozenState)
    document.removeEventListener('turbo:render', this.boundReapplyFrozenState)
    document.removeEventListener('turbo:before-stream-render', this.boundWrapStreamRender)
  }

  wrapStreamRender(event) {
    const target = event.target.target && document.getElementById(event.target.target)
    if (!target || !this.element.contains(target)) return

    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      this.reapplyFrozenState()
    }
  }

  reapplyFrozenState() {
    // Reset column order cache since DOM may have changed
    this.resetColumnOrder()
    const frozenColumns = this.getFrozenColumns()
    this.applyFrozenState(frozenColumns)
  }

  // ==================== Column Width Measuring ====================

  measureColumn(columnName) {
    const cells = [...this.getColumnCells(columnName)]
    if (cells.length === 0) return 0

    const container = document.createElement('div')
    container.style.cssText = 'position: absolute; top: 0; left: 0; visibility: hidden;'

    const measurers = cells.map((cell) => {
      const content = (cell.querySelector('.ps-2') || cell.firstElementChild || cell).cloneNode(true)
      content.querySelectorAll('[data-controller]').forEach((element) => element.removeAttribute('data-controller'))
      content.querySelectorAll('turbo-frame[src]').forEach((frame) => frame.removeAttribute('src'))

      const measurer = document.createElement('div')
      measurer.style.cssText = 'white-space: nowrap; width: max-content;'
      measurer.append(...content.childNodes)
      container.appendChild(measurer)
      return measurer
    })

    document.body.appendChild(container)
    const width = Math.max(...measurers.map((measurer) => measurer.offsetWidth)) + 24
    container.remove()

    return width
  }

  setupResizableColumns() {
    // Get all headers (frozen + scrollable)
    const headers = this.element.querySelectorAll('.task-table-header')

    headers.forEach(header => {
      const headerColumns = header.querySelectorAll('[data-task-table-target]')

      headerColumns.forEach((column) => {
        const columnName = this.getColumnName(column)
        if (!columnName) return

        // Skip if resize handle already exists
        if (column.querySelector('.column-resize-handle')) return

        // Create resize handle
        const resizeHandle = document.createElement('div')
        resizeHandle.className = 'column-resize-handle'
        resizeHandle.style.cssText = `
          position: absolute;
          top: 0;
          right: -4px;
          width: 8px;
          height: 100%;
          cursor: col-resize;
          user-select: none;
          z-index: 30;
          pointer-events: auto;
        `

        // Don't override position on frozen cells (they need sticky)
        // But ensure overflow is visible for the resize handle
        if (!column.classList.contains('task-cell-frozen')) {
          column.style.position = 'relative'
        } else {
          // For frozen cells, ensure overflow allows the resize handle to show
          column.style.overflow = 'visible'
        }
        column.appendChild(resizeHandle)

        // Drag functionality
        let startX, startWidth

        const startResize = (e) => {
          this.isResizing = true
          startX = e.pageX
          startWidth = column.offsetWidth
          resizeHandle.classList.add('resizing')

          document.addEventListener('mousemove', doResize)
          document.addEventListener('mouseup', stopResize)
          e.preventDefault()
        }

        const doResize = (e) => {
          if (!this.isResizing) return

          const diff = e.pageX - startX
          const newWidth = Math.max(startWidth + diff, columnName === 'title' ? 200 : 60)

          // Update CSS variable for this column
          const varName = this.getColumnVarName(columnName)
          this.element.style.setProperty(varName, `${newWidth}px`)

          // For custom columns, apply width directly to cells since CSS can't read dynamic variables
          if (this.isCustomColumn(columnName)) {
            this.applyCustomColumnWidth(columnName, newWidth)
          }

          // If this column is frozen, recalculate left positions for ALL frozen columns
          const frozenColumns = this.getFrozenColumns()
          if (frozenColumns.includes(columnName)) {
            // Recalculate cumulative left positions based on new widths
            const leftPositions = this.calculateLeftPositions(frozenColumns)

            // Apply new left positions to all frozen cells
            frozenColumns.forEach((frozenCol) => {
              const cells = this.getColumnCells(frozenCol)
              cells.forEach((cell) => {
                cell.style.left = `${leftPositions[frozenCol]}px`
              })
            })

            // Update total frozen width CSS variable
            this.updateFrozenWidth(frozenColumns)
          }
        }

        const stopResize = () => {
          this.isResizing = false
          resizeHandle.classList.remove('resizing')
          document.removeEventListener('mousemove', doResize)
          document.removeEventListener('mouseup', stopResize)
        }

        resizeHandle.addEventListener('mousedown', startResize)

        // Double-click to auto-fit column width
        resizeHandle.addEventListener('dblclick', (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.autoFitColumn(columnName)
        })
      })
    })
  }

  // Get all cells for a given column name
  getColumnCells(columnName) {
    // Check if this is a custom column (not a core column)
    const coreColumns = ['checkbox', 'title', 'priority', 'account', 'status', 'startDate', 'dueDate', 'assignee', 'clientAdvisor', 'taskType']
    if (!coreColumns.includes(columnName)) {
      // Custom column - query by data-column-id
      return this.element.querySelectorAll(`[data-task-table-target="customColumn"][data-column-id="${columnName}"]`)
    }
    // Core column - query by target name
    return this.element.querySelectorAll(`[data-task-table-target="${columnName}Column"]`)
  }

  autoFitColumn(columnName) {
    let maxWidth = this.measureColumn(columnName)

    // Apply minimum widths
    const minWidth = columnName === 'title' ? 200 : 60
    maxWidth = Math.max(maxWidth, minWidth)

    // Cap at a reasonable maximum
    maxWidth = Math.min(maxWidth, 500)

    // Update the CSS variable
    const varName = this.getColumnVarName(columnName)
    this.element.style.setProperty(varName, `${maxWidth}px`)

    // For custom columns, apply width directly to cells
    if (this.isCustomColumn(columnName)) {
      this.applyCustomColumnWidth(columnName, maxWidth)
    }

    // Update frozen width if this column is frozen
    const frozenColumns = this.getFrozenColumns()
    if (frozenColumns.includes(columnName)) {
      this.updateFrozenWidth(frozenColumns)
    }
  }

  // Check if a column is a custom (task type) column
  isCustomColumn(columnName) {
    const coreColumns = ['checkbox', 'title', 'priority', 'account', 'status', 'startDate', 'dueDate', 'assignee', 'clientAdvisor', 'taskType']
    return !coreColumns.includes(columnName)
  }

  // Apply width directly to custom column cells (since CSS can't read dynamic variables)
  applyCustomColumnWidth(columnName, width) {
    const cells = this.getColumnCells(columnName)
    cells.forEach((cell) => {
      cell.style.width = `${width}px`
      cell.style.minWidth = `${width}px`
      cell.style.flex = `0 0 ${width}px`
    })
  }

  getColumnVarName(columnName) {
    // Core column variable names (camelCase column names to CSS variable names)
    const coreVarNames = {
      'checkbox': '--checkbox-col-width',
      'title': '--title-col-width',
      'account': '--account-col-width',
      'status': '--status-col-width',
      'startDate': '--start-date-col-width',
      'dueDate': '--due-date-col-width',
      'assignee': '--assignee-col-width',
      'clientAdvisor': '--advisor-col-width',
      'taskType': '--type-col-width',
      'priority': '--priority-col-width'
    }
    // Custom columns get their own CSS variable based on column name
    // e.g., 'tax_year' -> '--tax_year-col-width'
    return coreVarNames[columnName] || `--${columnName}-col-width`
  }

  // ==================== Column Freezing ====================

  setupColumnFreezing() {
    // Reset column order to pick up any new columns
    this.resetColumnOrder()

    const header = this.element.querySelector('.task-table-header')
    if (!header) return

    const columns = header.querySelectorAll('[data-task-table-target]')
    columns.forEach((column) => {
      const columnName = this.getColumnName(column)
      if (!columnName) return
      // Skip checkbox column (always frozen, no button needed)
      if (columnName === 'checkbox') return
      // Add freeze indicator/button (including title for reset functionality)
      this.addFreezeButton(column, columnName)
    })
  }

  addFreezeButton(column, columnName) {
    const freezeBtn = document.createElement('button')
    freezeBtn.className = 'column-freeze-btn'
    freezeBtn.type = 'button'
    freezeBtn.title = columnName === 'title' ? 'Click to reset frozen columns' : 'Click to freeze/unfreeze columns up to here'
    freezeBtn.innerHTML = '<i class="fas fa-thumbtack"></i>'
    freezeBtn.style.cssText = `
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      background: transparent;
      border: none;
      padding: 2px 4px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s;
      color: #6c757d;
      font-size: 10px;
      z-index: 35;
    `

    // Show on hover, but respect always-visible state for last frozen column
    column.addEventListener('mouseenter', () => {
      freezeBtn.style.opacity = '1'
    })
    column.addEventListener('mouseleave', () => {
      // Keep visible if it's the last frozen column
      if (freezeBtn.dataset.alwaysVisible !== 'true') {
        freezeBtn.style.opacity = '0'
      }
    })

    // Toggle freeze on click
    freezeBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      // Immediately tilt the icon for visual feedback
      const icon = freezeBtn.querySelector('i')
      if (icon) icon.style.transform = 'rotate(45deg)'

      this.toggleFreezeUpTo(columnName)
    })

    // Ensure column has relative positioning for the button
    if (!column.classList.contains('task-cell-frozen')) {
      column.style.position = 'relative'
    }
    column.appendChild(freezeBtn)
  }

  toggleFreezeUpTo(columnName) {
    const currentFrozen = this.getFrozenColumns()
    const visibleColumns = this.getVisibleColumns()

    // Clicking title column resets to default (just title frozen)
    if (columnName === 'title') {
      const newFrozenColumns = ['title']
      this.saveFrozenState(newFrozenColumns)
      this.applyFrozenState(newFrozenColumns)
      return
    }

    // Use visible columns order for freeze calculations
    const columnIndex = visibleColumns.indexOf(columnName)

    if (columnIndex === -1) return

    const lastFrozenIndex = this.getLastFrozenIndexFromVisible(currentFrozen, visibleColumns)

    let newFrozenColumns
    if (columnIndex === lastFrozenIndex) {
      // Clicking the last frozen column - unfreeze it (go back one column)
      if (columnIndex > 0) {
        newFrozenColumns = visibleColumns.slice(0, columnIndex)
      } else {
        // Already at title, can't go back further - keep title frozen
        newFrozenColumns = visibleColumns.slice(0, 1)
      }
    } else {
      // Freeze up to this column (whether currently frozen or not)
      newFrozenColumns = visibleColumns.slice(0, columnIndex + 1)
    }

    this.saveFrozenState(newFrozenColumns)
    this.applyFrozenState(newFrozenColumns)
  }

  getLastFrozenIndexFromVisible(frozenColumns, visibleColumns) {
    let lastIndex = -1
    frozenColumns.forEach(col => {
      const idx = visibleColumns.indexOf(col)
      if (idx > lastIndex) lastIndex = idx
    })
    return lastIndex
  }

  getFrozenColumns() {
    try {
      const stored = localStorage.getItem(this.storageKeyValue)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Filter out columns that don't exist in current columnOrder
        const validColumns = parsed.filter(col => this.columnOrder.includes(col))
        return validColumns.length > 0 ? validColumns : ['title']
      }
      return ['title']
    } catch {
      return ['title']
    }
  }

  // Get columns that are currently visible in the DOM
  getVisibleColumns() {
    const header = this.element.querySelector('.task-table-header')
    if (!header) return this.columnOrder

    const visibleCols = []
    header.querySelectorAll('[data-task-table-target]').forEach(col => {
      const colName = this.getColumnName(col)
      if (colName && this.columnOrder.includes(colName)) {
        visibleCols.push(colName)
      }
    })
    return visibleCols.length > 0 ? visibleCols : this.columnOrder
  }

  saveFrozenState(frozenColumns) {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(frozenColumns))
    } catch {
      // localStorage not available
    }
  }

  restoreFrozenState() {
    const frozenColumns = this.getFrozenColumns()
    this.applyFrozenState(frozenColumns)
  }

  applyFrozenState(frozenColumns) {
    // Get all column cells across header, rows, and group headers
    const allCells = this.element.querySelectorAll('[data-task-table-target]')

    // Calculate cumulative left positions
    const leftPositions = this.calculateLeftPositions(frozenColumns)

    // Combine always-frozen columns with user-selected frozen columns
    const allFrozenColumns = [...this.alwaysFrozenColumns, ...frozenColumns.filter(c => !this.alwaysFrozenColumns.includes(c))]

    // Apply frozen state to all cells
    allCells.forEach((cell) => {
      const columnName = this.getColumnName(cell)
      if (!columnName) return

      const isFrozen = allFrozenColumns.includes(columnName)
      const isLastFrozen = columnName === frozenColumns[frozenColumns.length - 1]

      if (isFrozen) {
        cell.classList.add('task-cell-frozen')
        cell.style.left = `${leftPositions[columnName]}px`
        cell.style.position = 'sticky'
        cell.style.zIndex = this.getZIndexForCell(cell)

        // Add subtask-specific class for subtask rows
        if (cell.closest('.subtask-row')) {
          cell.classList.add('subtask-cell-frozen')
        }

        // Add border and shadow to last frozen column
        if (isLastFrozen) {
          cell.classList.add('task-cell-frozen-last')
          cell.style.borderRight = '2px solid #dee2e6'
        } else {
          cell.classList.remove('task-cell-frozen-last')
          cell.style.borderRight = ''
        }
      } else {
        cell.classList.remove('task-cell-frozen')
        cell.classList.remove('task-cell-frozen-last')
        cell.classList.remove('subtask-cell-frozen')
        cell.style.left = ''
        cell.style.position = ''
        cell.style.zIndex = '1' // Explicitly lower than frozen columns
        cell.style.borderRight = ''
        // Don't remove backgroundColor as it may be intentionally set
      }
    })

    // Update freeze button visibility
    this.updateFreezeButtonStates(frozenColumns)

    // Update CSS variable for total frozen width
    this.updateFrozenWidth(frozenColumns)
  }

  calculateLeftPositions(frozenColumns) {
    const positions = {}
    let cumulativeLeft = 0

    // Get the actual DOM order of columns from the header
    const visibleColumns = this.getVisibleColumns()

    // Combine always-frozen columns with user-selected frozen columns
    const allFrozenColumns = [...this.alwaysFrozenColumns, ...frozenColumns.filter(c => !this.alwaysFrozenColumns.includes(c))]

    // Calculate left positions based on actual DOM order
    // Each frozen column's left = sum of widths of all columns before it in DOM order
    visibleColumns.forEach((columnName) => {
      if (allFrozenColumns.includes(columnName)) {
        positions[columnName] = cumulativeLeft
      }
      // Always add width to cumulative, regardless of frozen state
      // This ensures frozen columns account for non-frozen columns before them
      cumulativeLeft += this.getColumnWidth(columnName)
    })

    return positions
  }

  getColumnWidth(columnName) {
    const varName = this.getColumnVarName(columnName)
    const cssValue = getComputedStyle(this.element).getPropertyValue(varName)

    if (cssValue) {
      return parseInt(cssValue, 10) || this.getDefaultWidth(columnName)
    }
    return this.getDefaultWidth(columnName)
  }

  getDefaultWidth(columnName) {
    // Core columns have specific widths (camelCase names)
    const coreDefaults = {
      'checkbox': 50,
      'title': 390,
      'priority': 50,
      'account': 150,
      'status': 140,
      'startDate': 120,
      'dueDate': 120,
      'assignee': 150,
      'clientAdvisor': 150,
      'taskType': 150
    }
    // Custom (task type) columns all default to 140px
    return coreDefaults[columnName] || 140
  }

  getZIndexForCell(cell) {
    // Higher z-index for header cells
    if (cell.closest('.task-table-header')) {
      return '31'
    }
    if (cell.closest('.group-header')) {
      return '21'
    }
    if (cell.closest('.subgroup-header')) {
      return '20'
    }
    // Subtask rows need same z-index as task rows
    if (cell.closest('.subtask-row')) {
      return '10'
    }
    return '10'
  }

  updateFreezeButtonStates(frozenColumns) {
    const lastFrozenColumn = frozenColumns[frozenColumns.length - 1]
    const hasExtraFrozenColumns = frozenColumns.length > 1 || (frozenColumns.length === 1 && frozenColumns[0] !== 'title')
    const buttons = this.element.querySelectorAll('.column-freeze-btn')

    buttons.forEach((btn) => {
      const column = btn.closest('[data-task-table-target]')
      if (!column) return

      const columnName = this.getColumnName(column)
      if (!columnName) return

      const isFrozen = frozenColumns.includes(columnName)
      const isLastFrozen = columnName === lastFrozenColumn
      const isTitleColumn = columnName === 'title'
      const icon = btn.querySelector('i')

      if (isTitleColumn) {
        // Title column: show pin when there are extra frozen columns (as reset button)
        if (hasExtraFrozenColumns) {
          btn.style.color = '#6c757d'
          btn.style.opacity = '1'
          btn.dataset.alwaysVisible = 'true'
          btn.title = 'Click to reset frozen columns'
          if (icon) icon.style.transform = '' // Upright for reset
        } else {
          btn.style.color = '#6c757d'
          btn.style.opacity = '0'
          btn.dataset.alwaysVisible = ''
          btn.title = 'Click to freeze/unfreeze columns up to here'
          if (icon) icon.style.transform = ''
        }
      } else if (isFrozen) {
        btn.style.color = '#0d6efd'

        // Keep the pin visible on the last frozen column
        if (isLastFrozen) {
          btn.style.opacity = '1'
          btn.dataset.alwaysVisible = 'true'
          if (icon) icon.style.transform = 'rotate(45deg)' // Tilted for active frozen
        } else {
          btn.style.opacity = '0'
          btn.dataset.alwaysVisible = ''
          if (icon) icon.style.transform = '' // Upright for other frozen columns
        }
      } else {
        btn.style.color = '#6c757d'
        btn.style.opacity = '0'
        btn.dataset.alwaysVisible = ''
        if (icon) icon.style.transform = '' // Upright for non-frozen
      }
    })
  }

  updateFrozenWidth(frozenColumns) {
    let totalWidth = 0

    // Include always-frozen columns first
    this.alwaysFrozenColumns.forEach((columnName) => {
      totalWidth += this.getColumnWidth(columnName)
    })

    // Then add user-selected frozen columns
    frozenColumns.forEach((columnName) => {
      if (this.alwaysFrozenColumns.includes(columnName)) return
      totalWidth += this.getColumnWidth(columnName)
    })

    this.element.style.setProperty('--frozen-width', `${totalWidth}px`)
  }
}
